import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Modal } from '../../components/UI/Modal';
import { Button } from '../../components/UI/Button';
import { AgentMentionAutocomplete } from '../../components/ChatInDoc/AgentMentionAutocomplete';
import { useSearchParams } from 'react-router-dom';
import { 
  ChatBubbleLeftRightIcon,
  DocumentIcon,
  FolderIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  TrashIcon, 
  DocumentMagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  StopIcon,
  PaperClipIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { DocumentNode } from '../../types/documents';
import { SearchPreview } from '../../types/chat';
import { ChatMessage } from '../../contexts/ChatInDocContext';
import { ChatSessionsList } from '../../components/ChatInDoc/ChatSessionsList';
import { DocumentSearch } from '../../components/ChatInDoc/DocumentSearch';
import { VoiceButton } from '../../components/ChatInDoc/VoiceButton';
import { MessageAudioControls } from '../../components/ChatInDoc/MessageAudioControls';
import { DocumentReferences } from '../../components/ChatInDoc/DocumentReferences';
import MessageRenderer from '../../components/ChatInDoc/MessageRenderer';
import { DocumentMessageBubble } from '../../components/ChatInDoc/DocumentMessageBubble';
import { voiceService } from '../../services/voiceService';
import { useToast } from '../../components/UI/ToastProvider';
import { FeatureAccessState } from '../../components/UI/FeatureAccessState';
import { mapWorkspaceHistoryMessage, useChatInDoc, type ChatInDocWorkspace } from '../../contexts/ChatInDocContext';
import { intelligentChatService } from '../../services/intelligentChatService';
import { workspaceService } from '../../services/workspaceService';
import { newChatInDocService, StreamChunk, type ChatVisionAttachmentRef } from '../../services/newChatInDocService';
import { api } from '../../services/api';
import { chatModelSupportsVision } from '../../utils/chatModelVision';
import { ChatVisionImages } from '../../components/ChatInDoc/ChatVisionImages';
import { userPrivateDocumentService } from '../../services/userPrivateDocumentService';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import SearchableSelect from '../../components/UI/SearchableSelect';
import { useCrossTenantSelector, getCustomerIdFromStorage, resolveExpectedContextTenantId } from '../../hooks/useCrossTenantSelector';
import { chatTenantService } from '../../services/chatTenantService';
import { WorkspaceAvatar } from '../../components/UI/WorkspaceAvatar';

type ViewMode = 'chat' | 'document-search';

const HEADER_AVATAR_HUES = [210, 145, 280, 35, 175];

function headerAvatarHue(workspaceId: string): number {
  let hash = 0;
  for (let i = 0; i < workspaceId.length; i++) {
    hash = workspaceId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return HEADER_AVATAR_HUES[Math.abs(hash) % HEADER_AVATAR_HUES.length];
}

function headerAvatarInitials(assistantName: string): string {
  const parts = assistantName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
  }
  return assistantName.trim().slice(0, 2).toUpperCase() || '?';
}

function parseMentionAtCursor(value: string, cursorPos: number): { atIndex: number; filter: string } | null {
  const before = value.slice(0, cursorPos);
  const at = before.lastIndexOf('@');
  if (at === -1) {
    return null;
  }
  const afterAt = before.slice(at + 1);
  if (/[\s\n]/.test(afterAt)) {
    return null;
  }
  return { atIndex: at, filter: afterAt };
}

type MentionSendRouting =
  | { kind: 'strip-marker'; marker: string }
  | { kind: 'new-thread'; workspaceId: string; marker: string }
  | { kind: 'pontual'; workspaceId: string; marker: string };

const ChatInDocPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const {
    threads,
    activeThreadId,
    setActiveThread,
    addThread,
    addMessage,
    updateMessage,
    isBackendConnected,
    connectionStatus,
    workspace,
    activeWorkspaceId,
    isLoadingWorkspace,
    chatWorkspaces,
    workspaceLoadError,
    isWorkspaceFeatureError,
    forceSelectWorkspace,
    refreshChatWorkspaces,
    resetForTenantChange,
  } = useChatInDoc();

  const {
    canSelectTenant,
    tenants,
    tenantOptions,
    selectedTenantId,
    handleTenantChange,
    tenantsLoading,
    isManagingOtherTenant,
  } = useCrossTenantSelector(async tenantId => {
    const storageCustomerId = getCustomerIdFromStorage();
    resetForTenantChange();
    chatTenantService.setActiveTenantId(tenantId === storageCustomerId ? null : tenantId);
    await refreshChatWorkspaces({ forceNewSelection: true });
  }, {
    getActiveContextTenantId: () => chatTenantService.getActiveTenantId(),
  });

  const resolveExpectedChatTenantServiceId = useCallback((tenantFromUrl: string | null) => {
    return resolveExpectedContextTenantId(tenantFromUrl ?? getCustomerIdFromStorage());
  }, []);

  const appliedUrlTenantKeyRef = useRef<string | null>(null);

  const syncTenantInUrl = useCallback((tenantId: string, clearWorkspace = false) => {
    const storageCustomerId = getCustomerIdFromStorage();
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (tenantId && tenantId !== storageCustomerId)
        {
          next.set('tenant', tenantId);
        }
        else
        {
          next.delete('tenant');
        }
        if (clearWorkspace)
        {
          next.delete('workspace');
        }
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const handleTenantChangeFromUi = useCallback((tenantId: string) => {
    if (tenantId === selectedTenantId)
    {
      return;
    }

    syncTenantInUrl(tenantId, true);
  }, [selectedTenantId, syncTenantInUrl]);

  const selectedTenantName = tenants.find(t => t.id === selectedTenantId)?.name;

  const headerAvatarStyle = useMemo(() => {
    if (!workspace?.id) {
      return { backgroundColor: 'hsl(210 48% 42%)' };
    }
    const hue = headerAvatarHue(workspace.id);
    return { backgroundColor: `hsl(${hue} 48% 42%)` };
  }, [workspace?.id]);
  
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [searchPreview, setSearchPreview] = useState<SearchPreview>({
    isOpen: false,
    document: null
  });
  const [viewMode, setViewMode] = useState<ViewMode>('chat');
  const [messageRatings, setMessageRatings] = useState<{[key: string]: 'like' | 'dislike' | null}>({});
  const [expandedReferences, setExpandedReferences] = useState<Record<string, boolean>>({});
  const [evaluatingMessages, setEvaluatingMessages] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const privateFileInputRef = useRef<HTMLInputElement>(null);
  const visionFileInputRef = useRef<HTMLInputElement>(null);
  const [pendingVisionImages, setPendingVisionImages] = useState<ChatVisionAttachmentRef[]>([]);
  const [assistantInterpretsImages, setAssistantInterpretsImages] = useState(false);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const mentionAtRef = useRef<number | null>(null);
  const mentionRoutingRef = useRef<MentionSendRouting | null>(null);
  const handleNewConversationFlowRef = useRef<
    (message: string, attachments?: ChatVisionAttachmentRef[]) => Promise<void>
  >(async () => undefined);
  const processMessageInThreadRef = useRef<
    (message: string, threadId: string, attachments?: ChatVisionAttachmentRef[]) => Promise<void>
  >(async () => undefined);
  const [privateUploadPercent, setPrivateUploadPercent] = useState<number | null>(null);
  const [privateUploading, setPrivateUploading] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionHighlight, setMentionHighlight] = useState(0);

  useEffect(() => {
    if (!activeWorkspaceId)
    {
      setAssistantInterpretsImages(false);
      return;
    }

    let cancelled = false;
    void workspaceService.getWorkspace(activeWorkspaceId).then((loaded) => {
      if (cancelled)
      {
        return;
      }

      setAssistantInterpretsImages(
        loaded.visionEnabled === true && chatModelSupportsVision(loaded.chatModel)
      );
    }).catch(() => {
      if (!cancelled)
      {
        setAssistantInterpretsImages(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [activeWorkspaceId]);
  const [agentIntentModal, setAgentIntentModal] = useState<{
    workspace: ChatInDocWorkspace;
    resolve: (v: 'new-thread' | 'pontual' | 'cancel') => void;
  } | null>(null);

  const filteredMentionWorkspaces = useMemo(() => {
    const q = mentionFilter.trim().toLowerCase();
    return chatWorkspaces.filter((w) => {
      if (w.isActive === false) {
        return false;
      }
      if (!q) {
        return true;
      }
      return (
        w.assistantName.toLowerCase().includes(q) ||
        w.name.toLowerCase().includes(q) ||
        w.slug.toLowerCase().includes(q) ||
        (w.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [chatWorkspaces, mentionFilter]);

  useEffect(() => {
    setMentionHighlight(0);
  }, [mentionFilter]);

  // Função dedicada para scroll automático
  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest'
        });
        
        // Scroll adicional para garantir que chegue ao final
        setTimeout(() => {
          const messagesContainer = messagesEndRef.current?.parentElement?.parentElement;
          if (messagesContainer) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
          }
        }, 10);
      }
    }, 100);
  };

  // Função para scroll instantâneo (sem animação)
  const scrollToBottomInstant = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'auto',
        block: 'end',
        inline: 'nearest'
      });
      
      // Scroll adicional para garantir que chegue ao final
      setTimeout(() => {
        const messagesContainer = messagesEndRef.current?.parentElement?.parentElement;
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }, 10);
    }
  };

  // Scroll automático sempre que houver mudanças nas mensagens
  useEffect(() => {
    if (threads.length > 0 && activeThreadId) {
      const activeThread = threads.find(t => t.id === activeThreadId);
      if (activeThread && activeThread.messages.length > 0) {
        // Usar scroll instantâneo para mensagens novas
        setTimeout(() => {
          scrollToBottomInstant();
        }, 10);
      }
    }
  }, [threads, activeThreadId]);

  // Scroll automático quando o indicador de digitação aparecer/desaparecer
  useEffect(() => {
    if (threads.length > 0 && activeThreadId) {
      const activeThread = threads.find(t => t.id === activeThreadId);
      if (activeThread && activeThread.messages.length > 0) {
        setTimeout(() => {
          scrollToBottom();
        }, 10);
      }
    }
  }, [isTyping, activeThreadId]);

  // Verificar se há uma pergunta na URL
  useEffect(() => {
    const queryFromUrl = searchParams.get('q');
    if (queryFromUrl && threads.length > 0) {
      // Iniciar conversa automaticamente
      setTimeout(() => {
        handleUserMessage(queryFromUrl);
      }, 500);
    }
  }, [searchParams, threads]);

  // Tenant é controlado pela URL: ?tenant=... ou tenant do usuário quando ausente
  useEffect(() => {
    if (tenantsLoading || !canSelectTenant)
    {
      return;
    }

    const tenantFromUrl = searchParams.get('tenant');
    const storageCustomerId = getCustomerIdFromStorage();
    const urlTenantKey = tenantFromUrl ?? storageCustomerId;
    const expectedServiceTenantId = resolveExpectedChatTenantServiceId(tenantFromUrl);
    const serviceAligned = chatTenantService.getActiveTenantId() === expectedServiceTenantId;

    if (!urlTenantKey || (appliedUrlTenantKeyRef.current === urlTenantKey && serviceAligned))
    {
      return;
    }

    appliedUrlTenantKeyRef.current = urlTenantKey;
    void handleTenantChange(tenantFromUrl ?? storageCustomerId);
  }, [
    searchParams,
    tenantsLoading,
    canSelectTenant,
    handleTenantChange,
    resolveExpectedChatTenantServiceId,
  ]);

  // Aplicar agente da URL após o tenant estar alinhado (ex.: vindo da tela de documento)
  useEffect(() => {
    const tenantFromUrl = searchParams.get('tenant');
    const workspaceFromUrl = searchParams.get('workspace');

    if (!workspaceFromUrl)
    {
      return;
    }

    if (tenantsLoading)
    {
      return;
    }

    const expectedServiceTenantId = resolveExpectedChatTenantServiceId(tenantFromUrl);
    if (canSelectTenant && chatTenantService.getActiveTenantId() !== expectedServiceTenantId)
    {
      return;
    }

    if (!chatWorkspaces.some(w => w.id === workspaceFromUrl))
    {
      return;
    }

    if (workspaceFromUrl !== activeWorkspaceId)
    {
      void forceSelectWorkspace(workspaceFromUrl);
    }
  }, [
    searchParams,
    tenantsLoading,
    canSelectTenant,
    chatWorkspaces,
    activeWorkspaceId,
    forceSelectWorkspace,
    resolveExpectedChatTenantServiceId,
  ]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        userPrivateDocumentId?: string;
        processingStatus?: string;
        threadId?: string;
        workspaceChatId?: string;
      };
      if (!detail?.threadId || detail.processingStatus == null || detail.processingStatus === '') {
        return;
      }
      const threadIdStr = String(detail.threadId);
      const thread = threads.find(t => t.id === threadIdStr);
      if (!thread) {
        return;
      }
      const workspaceChatIdStr = detail.workspaceChatId != null ? String(detail.workspaceChatId) : null;
      const docIdStr = detail.userPrivateDocumentId != null ? String(detail.userPrivateDocumentId) : null;
      const msg = thread.messages.find(
        m =>
          (workspaceChatIdStr && m.id === workspaceChatIdStr) ||
          (docIdStr && m.documentMetadata?.userPrivateDocumentId === docIdStr)
      );
      if (!msg?.documentMetadata) {
        return;
      }
      updateMessage(threadIdStr, msg.id, {
        documentMetadata: { ...msg.documentMetadata, processingStatus: detail.processingStatus }
      });
    };
    window.addEventListener('private-doc-indexing', handler as EventListener);
    return () => window.removeEventListener('private-doc-indexing', handler as EventListener);
  }, [threads, updateMessage]);

  // ✅ Função para recarregar mensagens da thread com IDs reais do banco
  const reloadThreadMessages = async (threadId: string) => {
    try {
      const history = await intelligentChatService.loadThreadMessages(threadId);
      
      if (history && history.length > 0) {
        // Mapear mensagens do banco para o formato ChatMessage
        const bankMessages: ChatMessage[] = history.map((msg: any) => mapWorkspaceHistoryMessage(msg));

        // Buscar thread atual para manter referências de documentos
        const currentThread = threads.find(t => t.id === threadId);
        if (currentThread) {
          bankMessages.forEach(bankMsg => {
            const existingMsg = currentThread.messages.find(m => m.id === bankMsg.id);
            if (existingMsg?.documentReferences?.length) {
              bankMsg.documentReferences = existingMsg.documentReferences;
            }
          });

          // Atualizar mensagens da thread
          currentThread.messages = bankMessages;
          
          // Forçar re-render
          setActiveThread(threadId);
        }
      }
    } catch (error) {
      console.error('Erro ao recarregar mensagens:', error);
    }
  };

  const syncMentionFromCursor = useCallback(() => {
    const el = messageInputRef.current;
    if (!el) {
      return;
    }
    const v = el.value;
    const cursor = el.selectionStart ?? v.length;
    const parsed = parseMentionAtCursor(v, cursor);
    if (parsed) {
      setMentionOpen(true);
      setMentionFilter(parsed.filter);
      mentionAtRef.current = parsed.atIndex;
    } else {
      setMentionOpen(false);
      mentionAtRef.current = null;
    }
  }, []);

  const insertMentionForWorkspace = useCallback((ws: ChatInDocWorkspace): string | null => {
    const el = messageInputRef.current;
    if (!el) {
      return null;
    }
    const value = el.value;
    const cursor = el.selectionStart ?? value.length;
    const atIdx = mentionAtRef.current ?? parseMentionAtCursor(value, cursor)?.atIndex;
    if (atIdx == null) {
      return null;
    }
    const marker = `@${ws.assistantName.trim()}`;
    const insertText = `${marker} `;
    const next = value.slice(0, atIdx) + insertText + value.slice(cursor);
    setNewMessage(next);
    setMentionOpen(false);
    mentionAtRef.current = null;
    requestAnimationFrame(() => {
      const pos = atIdx + insertText.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
    return marker;
  }, []);

  const resolveAgentIntent = useCallback(
    (ws: ChatInDocWorkspace): Promise<'new-thread' | 'pontual' | 'cancel'> => {
      if (!activeThreadId || chatWorkspaces.length === 1) {
        return Promise.resolve('new-thread');
      }
      return new Promise((resolve) => {
        setAgentIntentModal({ workspace: ws, resolve });
      });
    },
    [activeThreadId, chatWorkspaces.length]
  );

  const handleWorkspaceMentionPick = useCallback(
    async (ws: ChatInDocWorkspace) => {
      if (ws.id === activeWorkspaceId) {
        const marker = insertMentionForWorkspace(ws);
        if (marker) {
          mentionRoutingRef.current = { kind: 'strip-marker', marker };
        }
        return;
      }
      const intent = await resolveAgentIntent(ws);
      if (intent === 'cancel') {
        setMentionOpen(false);
        return;
      }
      const marker = insertMentionForWorkspace(ws);
      if (!marker) {
        return;
      }
      if (intent === 'new-thread') {
        mentionRoutingRef.current = { kind: 'new-thread', workspaceId: ws.id, marker };
      } else {
        mentionRoutingRef.current = { kind: 'pontual', workspaceId: ws.id, marker };
      }
    },
    [activeWorkspaceId, insertMentionForWorkspace, resolveAgentIntent]
  );

  const handleUserMessage = useCallback(async (
    message: string,
    options?: {
      streamMessage?: string;
      targetWorkspaceId?: string;
      attachments?: ChatVisionAttachmentRef[];
    }
  ) => {
    const attachments = options?.attachments ?? [];
    const displayText = message.trim();
    const streamText = (options?.streamMessage ?? message).trim();
    if (!displayText && attachments.length === 0)
    {
      return;
    }

    if (!streamText && attachments.length === 0)
    {
      return;
    }
    
    // Se não há thread ativa, pode ser uma nova conversa
    if (!activeThreadId)
    {
      await handleNewConversationFlowRef.current(displayText, attachments);
      return;
    }

    // Adicionar mensagem do usuário
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: displayText,
      timestamp: new Date(),
      rating: null,
      imageAttachments: attachments.length > 0
        ? attachments.map((item) => ({
          fileName: item.fileName,
          fileType: item.fileType,
          filePath: item.filePath,
          previewUrl: item.previewUrl,
        }))
        : undefined
    };
    
    addMessage(activeThreadId, userMessage);
    setNewMessage('');

    // Scroll automático para a nova mensagem
    setTimeout(() => {
      scrollToBottomInstant();
    }, 10);

    // Mostrar indicador de digitação
    setIsTyping(true);

    // Criar novo AbortController para esta mensagem
    abortControllerRef.current = new AbortController();

    // Criar mensagem do assistente para streaming
    let assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      rating: null,
      documentReferences: []
    };
    
    addMessage(activeThreadId, assistantMessage);

    try {
      let fullResponse = '';
      let sources: any[] = [];

      // Usar streaming via newChatInDocService
      await newChatInDocService.streamChat({
        threadId: activeThreadId,
        workspaceId: activeWorkspaceId ?? undefined,
        targetWorkspaceId: options?.targetWorkspaceId,
        message: streamText,
        attachments,
        userMessageContent: streamText !== displayText ? displayText : undefined,
        signal: abortControllerRef.current.signal,
        onChunk: (chunk: StreamChunk) => {
          console.log('🔍 CHUNK RECEBIDO:', chunk);
          console.log('🔍 CHUNK TYPE:', chunk.type);
          console.log('🔍 CHUNK TEXT:', chunk.textResponse);
          
          if (chunk.type === 'abort' || chunk.error) {
            setIsTyping(false);
            if (chunk.error) {
              addToast(chunk.error, 'error');
            }
            return;
          }

          if (chunk.type === 'stopGeneration') {
            setIsTyping(false);
            return;
          }

          // Atualizar mensagem do assistente com chunks recebidos
          if (chunk.type === 'textResponseChunk' && chunk.textResponse) {
            // ✅ CONCATENAR chunks em vez de substituir
            fullResponse += chunk.textResponse;
            
            // Atualizar mensagem no contexto
            updateMessage(activeThreadId, assistantMessageId, {
              content: fullResponse
            });
          }

          // Adicionar sources quando disponíveis
          if (chunk.sources && chunk.sources.length > 0) {
            sources = chunk.sources;
            
            // Mapear sources para documentReferences
            const documentReferences = chunk.sources.map((source: any) => ({
              id: source.id || source.chunkId || `source-${Date.now()}-${Math.random()}`,
              documentId: source.documentId,
              title: source.title || source.documentTitle || 'Documento',
              path: source.path || source.documentPath || source.title || 'Caminho não disponível',
              chunk: source.text || source.content || source.chunk || 'Conteúdo não disponível',
              score: source.score || source.similarity || 0.8,
              metadata: {
                ContentType: source.metadata?.ContentType || source.contentType || 'documento',
                CreatedAt: source.metadata?.CreatedAt || source.createdAt,
                Author: source.metadata?.Author || source.author || 'Sistema',
                FileName: source.metadata?.FileName || source.fileName || source.title,
                Size: source.metadata?.Size || 0,
                Updater: source.metadata?.Updater || 'Sistema',
                UpdatedAt: source.metadata?.UpdatedAt || source.updatedAt
              }
            }));
            
            updateMessage(activeThreadId, assistantMessageId, {
              documentReferences
            });
          }

          // Scroll automático durante streaming
          setTimeout(() => {
            scrollToBottomInstant();
          }, 10);
        },
        onError: (error) => {
          console.error('Erro no streaming:', error);
          addToast('Erro ao processar mensagem', 'error');
          setIsTyping(false);
        },
        onComplete: async () => {
          setIsTyping(false);
          
          // ✅ RECARREGAR MENSAGENS para obter IDs reais do banco
          if (activeThreadId) {
            await reloadThreadMessages(activeThreadId);
          }
          
          setTimeout(() => {
            scrollToBottomInstant();
          }, 10);
        }
      });

    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      
      // Atualizar mensagem com erro
      updateMessage(activeThreadId, assistantMessageId, {
        content: "Desculpe, ocorreu um erro ao processar sua pergunta. Tente novamente em alguns instantes."
      });

      addToast('Erro ao processar mensagem', 'error');
    } finally {
      setIsTyping(false);
      abortControllerRef.current = null;
    }

  }, [activeThreadId, activeWorkspaceId, addMessage, updateMessage, addToast, threads, reloadThreadMessages]);

  // Novo método para lidar com nova conversa
  const handleNewConversationFlow = useCallback(async (
    message: string,
    attachments: ChatVisionAttachmentRef[] = []
  ) => {
    try {
      setIsTyping(true);

      const titleSource = message.trim() || attachments[0]?.fileName || 'Imagem';
      const title = titleSource.length > 50
        ? titleSource.substring(0, 47) + '...'
        : titleSource;

      const threadResponse = await intelligentChatService.startIntelligentThread({
        workspaceId: activeWorkspaceId || '',
        title: title,
        initialMessage: message
      });

      const newThreadId = threadResponse.threadId;

      addThread(threadResponse.title, newThreadId);
      setActiveThread(newThreadId);

      await processMessageInThreadRef.current(message, newThreadId, attachments);

    } catch (error: any) {
      addToast(error.message || 'Erro ao iniciar nova conversa', 'error');
    } finally {
      setIsTyping(false);
    }
  }, [activeWorkspaceId, addThread, setActiveThread, addToast]);

  handleNewConversationFlowRef.current = handleNewConversationFlow;

  // Função para processar mensagem em uma thread específica (sem verificar activeThreadId)
  const processMessageInThread = useCallback(async (
    message: string,
    threadId: string,
    attachments: ChatVisionAttachmentRef[] = []
  ) => {
    if (!message.trim() && attachments.length === 0)
    {
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date(),
      rating: null,
      imageAttachments: attachments.length > 0
        ? attachments.map((item) => ({
          fileName: item.fileName,
          fileType: item.fileType,
          filePath: item.filePath,
          previewUrl: item.previewUrl,
        }))
        : undefined
    };
    
    addMessage(threadId, userMessage);
    setNewMessage('');

    // Scroll automático para a nova mensagem
    setTimeout(() => {
      scrollToBottomInstant();
    }, 10);

    // Mostrar indicador de digitação
    setIsTyping(true);

    // Criar novo AbortController
    abortControllerRef.current = new AbortController();

    // Criar mensagem do assistente para streaming
    let assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      rating: null,
      documentReferences: []
    };
    
    addMessage(threadId, assistantMessage);

    try {
      // Usar streaming via newChatInDocService
      await newChatInDocService.streamChat({
        threadId: threadId,
        workspaceId: activeWorkspaceId ?? undefined,
        message: message,
        attachments: attachments.length > 0 ? attachments : undefined,
        signal: abortControllerRef.current.signal,
        onChunk: (chunk: StreamChunk) => {
          console.log('📥 Chunk recebido:', chunk);
          
          // Lidar com erros e aborts
          if (chunk.type === 'abort' || chunk.error) {
            setIsTyping(false);
            if (chunk.error) {
              addToast(chunk.error, 'error');
            }
            return;
          }

          if (chunk.type === 'stopGeneration') {
            setIsTyping(false);
            return;
          }

          // ⚡ NOVO: Lidar com evento de SOURCES (enviado primeiro)
          if (chunk.type === 'sources') {
            console.log('📚 Sources recebidas:', chunk.sources);
            if (chunk.sources && chunk.sources.length > 0) {
              const documentReferences = chunk.sources.map((source: any) => ({
                id: source.id || source.chunkId || `source-${Date.now()}-${Math.random()}`,
                documentId: source.documentId,
                title: source.title || source.documentTitle || 'Documento',
                path: source.path || source.documentPath || source.title || 'Caminho não disponível',
                chunk: source.text || source.content || source.chunk || 'Conteúdo não disponível',
                score: source.score || source.similarity || 0.8,
                metadata: {
                  ContentType: source.metadata?.ContentType || source.contentType || 'documento',
                  CreatedAt: source.metadata?.CreatedAt || source.createdAt,
                  Author: source.metadata?.Author || source.author || 'Sistema',
                  FileName: source.metadata?.FileName || source.fileName || source.title,
                  Size: source.metadata?.Size || 0,
                  Updater: source.metadata?.Updater || 'Sistema',
                  UpdatedAt: source.metadata?.UpdatedAt || source.updatedAt
                }
              }));
              
              updateMessage(threadId, assistantMessageId, {
                documentReferences
              });
            }
            return;
          }

          // ⚡ NOVO: Lidar com CHUNKS DE TEXTO (stream real)
          if (chunk.type === 'textResponseChunk') {
            console.log('💬 Chunk de texto:', chunk.textResponse);
            updateMessage(threadId, assistantMessageId, {
              content: chunk.textResponse
            });
            
            // Se for o último chunk
            if (chunk.close) {
              setIsTyping(false);
            }
            return;
          }

          // ⚡ NOVO: Lidar com evento de STREAM COMPLETO
          if (chunk.type === 'streamComplete') {
            console.log('✅ Stream completo');
            setIsTyping(false);
            return;
          }

          // ⚠️ FALLBACK: Compatibilidade com formato antigo
          if (chunk.textResponse) {
            console.log('⚠️ Formato antigo detectado');
            updateMessage(threadId, assistantMessageId, {
              content: chunk.textResponse
            });
            
            // Adicionar sources quando disponíveis (formato antigo)
            if (chunk.sources && chunk.sources.length > 0) {
              const documentReferences = chunk.sources.map((source: any) => ({
                id: source.id || source.chunkId || `source-${Date.now()}-${Math.random()}`,
                documentId: source.documentId,
                title: source.title || source.documentTitle || 'Documento',
                path: source.path || source.documentPath || source.title || 'Caminho não disponível',
                chunk: source.text || source.content || source.chunk || 'Conteúdo não disponível',
                score: source.score || source.similarity || 0.8,
                metadata: {
                  ContentType: source.metadata?.ContentType || source.contentType || 'documento',
                  CreatedAt: source.metadata?.CreatedAt || source.createdAt,
                  Author: source.metadata?.Author || source.author || 'Sistema',
                  FileName: source.metadata?.FileName || source.fileName || source.title,
                  Size: source.metadata?.Size || 0,
                  Updater: source.metadata?.Updater || 'Sistema',
                  UpdatedAt: source.metadata?.UpdatedAt || source.updatedAt
                }
              }));
              
              updateMessage(threadId, assistantMessageId, {
                documentReferences
              });
            }
          }

          // Scroll automático
          setTimeout(() => {
            scrollToBottomInstant();
          }, 10);
        },
        onError: (error) => {
          console.error('Erro no streaming:', error);
          addToast('Erro ao processar mensagem', 'error');
          setIsTyping(false);
        },
        onComplete: async () => {
          setIsTyping(false);
          
          // ✅ RECARREGAR MENSAGENS para obter IDs reais do banco
          await reloadThreadMessages(threadId);
          
          setTimeout(() => {
            scrollToBottomInstant();
          }, 10);
        }
      });

    } catch (error: any) {
      console.error('Erro ao processar mensagem:', error);
      
      updateMessage(threadId, assistantMessageId, {
        content: 'Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.'
      });
      
      addToast('Erro ao processar mensagem', 'error');
    } finally {
      setIsTyping(false);
      abortControllerRef.current = null;
    }
  }, [threads, activeWorkspaceId, addMessage, updateMessage, addToast, reloadThreadMessages]);

  processMessageInThreadRef.current = processMessageInThread;

  const handleSearchInChats = (query: string) => {
    // Implementação futura de busca em conversas
  };

  const handleShowDocumentSearch = () => {
    setViewMode('document-search');
  };

  const handleBackToChat = () => {
    setViewMode('chat');
  };

  const handleVoiceResult = (text: string) => {
    setNewMessage(text);
  };

  const handleVoiceError = (error: string) => {
    addToast(`Erro na gravação: ${error}`, 'error');
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    addToast(message, type);
  };

  const handleVisionFilesSelected = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0 || !activeWorkspaceId)
    {
      return;
    }

    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    for (const file of files)
    {
      const mime = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
      if (!allowed.includes(mime))
      {
        addToast('Use JPEG, PNG ou WebP.', 'error');
        continue;
      }

      if (file.size > 5 * 1024 * 1024)
      {
        addToast('A imagem passa de 5 MB.', 'error');
        continue;
      }

      const form = new FormData();
      form.append('workspaceId', activeWorkspaceId);
      const customerId = chatTenantService.buildCustomerIdParams().customerId;
      if (customerId)
      {
        form.append('customerId', customerId);
      }
      form.append('file', file);
      try
      {
        const response = await api.post<{
          fileName: string;
          fileType: string;
          fileSize: number;
          filePath: string;
        }>('/chatindoc/vision-attachments', form);
        const uploaded = response.data;
        setPendingVisionImages((current) => [
          ...current,
          {
            fileName: uploaded.fileName || file.name,
            fileType: uploaded.fileType || mime,
            fileSize: uploaded.fileSize || file.size,
            filePath: uploaded.filePath,
            previewUrl: URL.createObjectURL(file),
          },
        ]);
      }
      catch (error: unknown)
      {
        addToast(apiErrorMessage(error, 'Não foi possível enviar a imagem.'), 'error');
      }
    }
  }, [activeWorkspaceId, addToast]);

  const handleSendMessage = useCallback(() => {
    const raw = newMessage.trim();
    const attachments = pendingVisionImages;
    if (!raw && attachments.length === 0)
    {
      return;
    }

    const r = mentionRoutingRef.current;
    if (attachments.length > 0 && r)
    {
      addToast('Envie a imagem nesta conversa, sem mencionar outro agente.', 'error');
      return;
    }

    let streamText = raw;
    if (r?.marker && raw.includes(r.marker)) {
      streamText = raw.split(r.marker).join('').replace(/\s+/g, ' ').trim();
    }
    if (!streamText.trim() && (attachments.length === 0 || r)) {
      addToast('Escreva uma pergunta além do @agente.', 'error');
      return;
    }
    if (r?.kind === 'new-thread') {
      mentionRoutingRef.current = null;
      void (async () => {
        try {
          await forceSelectWorkspace(r.workspaceId);
          const title = streamText.length > 50 ? streamText.slice(0, 47) + '...' : streamText;
          const tid = await addThread(title, undefined, r.workspaceId);
          setNewMessage('');
          await processMessageInThread(streamText, tid);
        } catch (err: unknown) {
          addToast(err instanceof Error ? err.message : 'Falha ao abrir conversa no outro agente', 'error');
        }
      })();
      return;
    }
    mentionRoutingRef.current = null;
    const targetWorkspaceId = r?.kind === 'pontual' ? r.workspaceId : undefined;
    setPendingVisionImages([]);
    void handleUserMessage(raw, { streamMessage: streamText, targetWorkspaceId, attachments });
  }, [
    newMessage,
    pendingVisionImages,
    addToast,
    forceSelectWorkspace,
    addThread,
    processMessageInThread,
    handleUserMessage,
  ]);

  const handleMessageKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (mentionOpen && filteredMentionWorkspaces.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionHighlight((i) => Math.min(i + 1, filteredMentionWorkspaces.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionHighlight((i) => Math.max(0, i - 1));
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          const pick = filteredMentionWorkspaces[mentionHighlight];
          if (pick) {
            void handleWorkspaceMentionPick(pick);
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMentionOpen(false);
          return;
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [
      mentionOpen,
      filteredMentionWorkspaces,
      mentionHighlight,
      handleWorkspaceMentionPick,
      handleSendMessage,
    ]
  );

  const handlePrivateFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setPrivateUploading(true);
    setPrivateUploadPercent(0);
    try {
      const threadForUpload = activeThreadId;
      const dto = await userPrivateDocumentService.upload(
        file,
        pct => setPrivateUploadPercent(pct),
        threadForUpload ?? undefined
      );
      if (threadForUpload) {
        await reloadThreadMessages(threadForUpload);
        setTimeout(() => scrollToBottomInstant(), 50);
      } else {
        addToast(
          `"${dto.fileName}" enviado. A indexação ocorre em segundo plano; em seguida o documento entra nas buscas do chat.`,
          'success'
        );
      }
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number } };
      const status = ax.response?.status;
      if (status === 403) {
        addToast('Quota de armazenamento do tenant não configurada. Contate o administrador.', 'error');
      } else if (status === 413) {
        addToast(apiErrorMessage(err, 'Limite de armazenamento excedido.'), 'error');
      } else {
        addToast(apiErrorMessage(err, 'Falha ao enviar arquivo para o drive privado.'), 'error');
      }
    } finally {
      setPrivateUploading(false);
      setPrivateUploadPercent(null);
    }
  };

  const handleStopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsTyping(false);
      addToast('Geração interrompida', 'info');
    }
  };

  const openDocumentPreview = (document: DocumentNode) => {
    setSearchPreview({
      isOpen: true,
      document
    });
  };

  const closeDocumentPreview = () => {
    setSearchPreview({
      isOpen: false,
      document: null
    });
  };

  // Nova função para perguntar sobre um documento específico
  const askAboutDocument = (document: DocumentNode) => {
    // Criar nova thread específica para o documento
    addThread(`Chat sobre: ${document.title}`);
    
    // Fechar o preview do documento
    closeDocumentPreview();
    
    // Fazer a pergunta automática sobre o documento
    const question = `Fale mais sobre o documento "${document.title}"`;
    handleUserMessage(question);
  };

  const getFileIcon = (nodeType: string) => {
    return nodeType === 'folder' ? (
      <FolderIcon className="h-5 w-5 text-yellow-500" />
    ) : (
      <DocumentIcon className="h-5 w-5 text-blue-500" />
    );
  };

  const formatDate = (date: string) => {
    if (date && date.includes('/') && date.includes(':')) {
      return date;
    }
    
    try {
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleDateString('pt-BR');
      }
    } catch (error) {
      // Erro silencioso no parse da data
    }
    
    return 'Data não disponível';
  };

  const handleCopyMessage = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      addToast('Copiado!', 'success', 2000);
    } catch (error) {
      addToast('Erro ao copiar', 'error', 2000);
    }
  };

  // Função para determinar o rating atual (prioridade: estado local > rating da mensagem)
  const getCurrentRating = (messageId: string, messageRating: 'like' | 'dislike' | null): 'like' | 'dislike' | null => {
    // Se existe uma entrada no estado local (incluindo null), usar ela
    if (messageId in messageRatings) {
      return messageRatings[messageId];
    }
    // Senão, usar o rating da mensagem
    return messageRating;
  };

  const handleMessageRating = async (messageId: string, rating: 'like' | 'dislike') => {
    // Encontrar a mensagem para obter o rating atual
    const currentThread = threads.find(t => t.id === activeThreadId);
    const foundMessage = currentThread?.messages.find(m => m.id === messageId);
    
    if (!foundMessage) {
      addToast('Erro: mensagem não encontrada.', 'error');
      return;
    }

    // Adicionar mensagem ao estado de loading
    setEvaluatingMessages(prev => new Set(prev).add(messageId));

    try {
      // Determinar o valor da avaliação atual usando a função centralizada
      const currentRating = getCurrentRating(messageId, foundMessage.rating);
      let evaluationValue: boolean | null = null;

      if (currentRating === rating) {
        // Se clicou no mesmo botão, remove a avaliação (null)
        evaluationValue = null;
      } else {
        // Se clicou em um botão diferente, define a nova avaliação
        evaluationValue = rating === 'like';
      }

      // Chamar a API com o messageId (que agora é o GUID correto)
      await workspaceService.evaluateChatMessage(messageId, evaluationValue);

      // Atualizar o estado local apenas se a API foi bem-sucedida
      setMessageRatings(prev => {
        if (evaluationValue === null) {
          // Quando remove a avaliação, definir explicitamente como null para sobrescrever o rating original
          return { ...prev, [messageId]: null };
        }
        return { ...prev, [messageId]: rating };
      });

      // Mostrar notificação de sucesso
      const successMessage = evaluationValue === null 
        ? 'Avaliação removida' 
        : `Avaliação registrada: ${rating === 'like' ? 'Gostei' : 'Não gostei'}`;
      
      addToast(successMessage, 'success');
    } catch (error) {
      addToast('Erro ao registrar avaliação. Tente novamente.', 'error');
    } finally {
      // Remover mensagem do estado de loading
      setEvaluatingMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  const toggleReferencesVisibility = (messageId: string) => {
    setExpandedReferences(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  if (viewMode === 'document-search') {
    return (
      <div className="flex h-screen">
        <DocumentSearch
          onBackToChat={handleBackToChat}
          onOpenDocumentPreview={openDocumentPreview}
        />
      </div>
    );
  }

  // Se estiver carregando o workspace, mostrar loading
  const isWorkspaceResolving =
    isBackendConnected &&
    !workspace &&
    (isLoadingWorkspace || chatWorkspaces.length > 0);
  if (isLoadingWorkspace || isWorkspaceResolving) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-6"></div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Carregando Copiloto</h1>
            <p className="text-gray-600 mb-6">Inicializando assistente virtual...</p>
          </div>
        </div>
      </div>
    );
  }

  // Se o backend não estiver conectado, mostrar mensagem de erro
  if (!isBackendConnected) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Servidor não respondeu</h1>
            <p className="text-gray-600 mb-6">{connectionStatus}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-lg"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Se o workspace não foi carregado, mostrar erro
  if (!workspace) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 flex items-center justify-center bg-gray-50 px-6">
          <FeatureAccessState
            variant={isWorkspaceFeatureError ? 'feature' : 'error'}
            title={isWorkspaceFeatureError ? 'Funcionalidade indisponível' : 'Erro ao carregar workspace'}
            message={workspaceLoadError ?? 'Não foi possível carregar as configurações do assistente.'}
            onRetry={isWorkspaceFeatureError ? undefined : () => void refreshChatWorkspaces()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Lista de Conversas */}
      <ChatSessionsList
        onShowDocumentSearch={handleShowDocumentSearch}
        agentSwitchDisabled={isTyping}
      />

      {/* Chat Area */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          {canSelectTenant && (
            <div className="mb-4 w-72 max-w-full">
              <SearchableSelect
                options={tenantOptions}
                value={selectedTenantId || null}
                onChange={handleTenantChangeFromUi}
                placeholder="Selecione um cliente"
                searchPlaceholder="Buscar cliente..."
                emptyMessage="Nenhum cliente encontrado"
                disabled={tenantsLoading}
                ariaLabel="Selecionar cliente para visualizar agentes do chat"
              />
            </div>
          )}

          {isManagingOtherTenant && (
            <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <span>
                Você está acessando o copilot do cliente{' '}
                <strong>{selectedTenantName}</strong>. As conversas e agentes exibidos pertencem a esse cliente.
              </span>
            </div>
          )}

          <div className="flex items-start gap-3 min-w-0">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-600">
                Conversa ativa com{' '}
                <span className="font-medium text-gray-800">{workspace.assistantName}</span>
                {activeThreadId && threads.find((t) => t.id === activeThreadId)?.title
                  ? ` · ${threads.find((t) => t.id === activeThreadId)!.title}`
                  : ''}
              </p>
              <div className="mt-3 flex items-center gap-3">
                <WorkspaceAvatar
                  workspaceId={workspace.id}
                  avatar={workspace.avatar}
                  alt={workspace.assistantName}
                  className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0"
                  imgClassName="w-12 h-12 rounded-full object-cover flex-shrink-0"
                  fallbackStyle={headerAvatarStyle}
                >
                  {headerAvatarInitials(workspace.assistantName)}
                </WorkspaceAvatar>
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold text-gray-900 truncate">
                    {workspace.assistantName}
                  </h1>
                  {(workspace.description || '').trim() ? (
                    <p className="text-sm text-gray-600 line-clamp-2" title={workspace.description}>
                      {workspace.description.trim()}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500">Assistente virtual</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto bg-gray-50 scroll-smooth min-h-0">
          <div className="p-6 space-y-4">
            {activeThreadId ? (
              <>
                {threads.find(t => t.id === activeThreadId)?.messages.map((message) => {
                  const userHasImages = message.role === 'user'
                    && (message.imageAttachments?.length ?? 0) > 0
                    && message.messageType !== 'document';

                  return (
                  <div key={message.id} className="flex">
                    <div className={`flex-1 ${message.role === 'user' ? 'flex justify-end' : ''}`}>
                      <div
                        className={`relative rounded-lg overflow-hidden ${
                          userHasImages
                            ? 'w-full max-w-sm bg-blue-600 text-white ml-auto p-2'
                            : message.role === 'user'
                              ? 'max-w-4xl px-4 py-3 bg-blue-600 text-white ml-auto'
                              : 'max-w-4xl px-4 py-3 bg-white text-gray-900 shadow-sm'
                        }`}
                      >
                        {/* Header com ícones de ação */}
                        {!userHasImages && (
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1"></div>
                          <div className="flex items-center space-x-1">
                            {/* Like/Dislike - só para mensagens do assistente E quando não está digitando */}
                            {message.role === 'assistant' && message.content && !isTyping && (
                              <>
                                <button
                                  onClick={() => handleMessageRating(message.id, 'like')}
                                  disabled={evaluatingMessages.has(message.id)}
                                  className={`p-1 rounded transition-colors ${
                                    getCurrentRating(message.id, message.rating) === 'like'
                                      ? 'text-green-500'
                                      : 'text-gray-400 hover:text-gray-600'
                                  } ${evaluatingMessages.has(message.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  title="Gostei da resposta"
                                >
                                  {evaluatingMessages.has(message.id) ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                  ) : (
                                    <HandThumbUpIcon className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleMessageRating(message.id, 'dislike')}
                                  disabled={evaluatingMessages.has(message.id)}
                                  className={`p-1 rounded transition-colors ${
                                    getCurrentRating(message.id, message.rating) === 'dislike'
                                      ? 'text-red-500'
                                      : 'text-gray-400 hover:text-gray-600'
                                  } ${evaluatingMessages.has(message.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  title="Não gostei da resposta"
                                >
                                  {evaluatingMessages.has(message.id) ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                  ) : (
                                    <HandThumbDownIcon className="h-4 w-4" />
                                  )}
                                </button>
                              </>
                            )}
                            {/* Ícone de cópia */}
                            <button
                              onClick={() => handleCopyMessage(message.content)}
                              className={`p-1 rounded transition-colors ${
                                message.role === 'user'
                                  ? 'text-blue-200 hover:text-white'
                                  : 'text-gray-400 hover:text-gray-600'
                              }`}
                              title="Copiar mensagem"
                            >
                              <ClipboardDocumentIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        )}
                        
                        {/* Conteúdo da mensagem */}
                        {message.role === 'user' &&
                        message.messageType === 'document' &&
                        message.documentMetadata ? (
                          <DocumentMessageBubble metadata={message.documentMetadata} variant="user" />
                        ) : (
                          <>
                            {message.imageAttachments && message.imageAttachments.length > 0 && (
                              <ChatVisionImages
                                attachments={message.imageAttachments}
                                workspaceId={activeWorkspaceId}
                                flush={userHasImages}
                              />
                            )}
                            {message.content ? (
                              userHasImages ? (
                                <div className="mt-1.5 flex items-start gap-2 px-1">
                                  <MessageRenderer
                                    content={message.content}
                                    role={message.role}
                                    className="mb-0 min-w-0 flex-1"
                                  />
                                  <div className="flex items-center">
                                    <button
                                      type="button"
                                      onClick={() => handleCopyMessage(message.content)}
                                      className="p-1 text-blue-200 hover:text-white"
                                      title="Copiar mensagem"
                                    >
                                      <ClipboardDocumentIcon className="h-4 w-4" />
                                    </button>
                                    <MessageAudioControls
                                      text={message.content}
                                      messageType={message.role}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <MessageRenderer content={message.content} role={message.role} />
                              )
                            ) : null}
                          </>
                        )}
                        
                        {/* Link discreto para referências (só para mensagens do assistente com referências) */}
                        {message.role === 'assistant' && message.documentReferences && message.documentReferences.length > 0 && (
                          <div className="flex items-center justify-between mt-1 mb-2">
                            <button
                              onClick={() => toggleReferencesVisibility(message.id)}
                              className="text-xs text-blue-500 hover:text-blue-700 hover:underline flex items-center transition-colors"
                            >
                              Referências ({message.documentReferences.length})
                              {expandedReferences[message.id] ? (
                                <ChevronUpIcon className="h-3 w-3 ml-1" />
                              ) : (
                                <ChevronDownIcon className="h-3 w-3 ml-1" />
                              )}
                            </button>
                          </div>
                        )}
                        
                        {/* Referências dos documentos (só para mensagens do assistente) */}
                        {message.role === 'assistant' && message.documentReferences && (
                          <DocumentReferences 
                            references={message.documentReferences}
                            isVisible={expandedReferences[message.id] || false}
                          />
                        )}
                        
                        {/* Footer com data e controles de áudio */}
                        <div className={`flex items-center justify-between ${userHasImages ? 'px-1 pb-0.5' : ''}`}>
                          <span className="text-xs opacity-70">
                            {message.timestamp.toLocaleTimeString()}
                          </span>
                          {!userHasImages && (
                            <MessageAudioControls
                              text={message.content}
                              messageType={message.role}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
                
                {/* Typing Indicator */}
                {isTyping && (
                  <div className="flex">
                    <div className="bg-white text-gray-900 px-4 py-3 rounded-lg shadow-sm">
                      <div className="flex items-center space-x-2">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                        <span className="text-sm text-gray-500">Copiloto está digitando...</span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <ChatBubbleLeftRightIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Selecione uma conversa</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Selecione uma conversa ao lado para iniciar ou continuar
                  {threads.length === 0
                    ? ` ou inicie uma nova com ${workspace.assistantName} pelo botão Nova Conversa na barra lateral.`
                    : '.'}
                </p>
              </div>
            )}
            
            <div ref={messagesEndRef} className="h-4" />
          </div>
        </div>

        {/* Input Area */}
        {activeThreadId ? (
          <div className="flex-shrink-0 bg-white border-t border-gray-100">
            <div className="px-6 py-4">
              <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                <input
                  ref={privateFileInputRef}
                  type="file"
                  className="hidden"
                  onChange={e => void handlePrivateFileSelected(e)}
                />
                <input
                  ref={visionFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={e => void handleVisionFilesSelected(e)}
                />
                {pendingVisionImages.length > 0 && (
                  <div className="mb-3">
                    {!assistantInterpretsImages && (
                      <p className="mb-2 text-xs text-amber-700">
                        O assistente não vai interpretar esta imagem. O arquivo fica na conversa.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {pendingVisionImages.map((item) => (
                        <div key={item.filePath} className="relative">
                          <img
                            src={item.previewUrl}
                            alt={item.fileName}
                            className="h-16 w-16 rounded object-cover"
                          />
                          <button
                            type="button"
                            className="absolute -right-1 -top-1 rounded-full bg-white text-xs text-gray-600 shadow px-1"
                            onClick={() => setPendingVisionImages((current) => current.filter((image) => image.filePath !== item.filePath))}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {privateUploadPercent !== null && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Enviando para o drive privado...</span>
                      <span>{privateUploadPercent}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className="h-full bg-blue-600 transition-all duration-150"
                        style={{ width: `${privateUploadPercent}%` }}
                      />
                    </div>
                  </div>
                )}
                <div className="flex items-end space-x-4">
                  <div className="flex-1 relative">
                    {mentionOpen ? (
                      <AgentMentionAutocomplete
                        items={filteredMentionWorkspaces}
                        highlightedIndex={mentionHighlight}
                        onHighlightChange={setMentionHighlight}
                        onPick={(w) => void handleWorkspaceMentionPick(w)}
                      />
                    ) : null}
                    <textarea
                      ref={messageInputRef}
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        const cursor = e.target.selectionStart ?? e.target.value.length;
                        const parsed = parseMentionAtCursor(e.target.value, cursor);
                        if (parsed) {
                          setMentionOpen(true);
                          setMentionFilter(parsed.filter);
                          mentionAtRef.current = parsed.atIndex;
                        } else {
                          setMentionOpen(false);
                          mentionAtRef.current = null;
                        }
                      }}
                      onKeyDown={handleMessageKeyDown}
                      onClick={syncMentionFromCursor}
                      onKeyUp={syncMentionFromCursor}
                      placeholder="Digite sua pergunta. Use @ para mencionar um agente…"
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-32 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={1}
                      style={{ minHeight: '44px', maxHeight: '120px' }}
                      disabled={!activeThreadId || isTyping || privateUploading}
                    />
                    <button
                      type="button"
                      onClick={() => visionFileInputRef.current?.click()}
                      disabled={!activeThreadId || isTyping || privateUploading}
                      className="absolute right-20 bottom-2 p-2 text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                      title="Anexar imagem (JPEG, PNG ou WebP)"
                    >
                      <PhotoIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => privateFileInputRef.current?.click()}
                      disabled={!activeThreadId || isTyping || privateUploading}
                      className="absolute right-12 bottom-2 p-2 text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                      title="Enviar arquivo para o drive privado (uso no chat após indexação)"
                    >
                      <PaperClipIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        handleSendMessage();
                      }}
                      disabled={(!newMessage.trim() && pendingVisionImages.length === 0) || isTyping || !activeThreadId || privateUploading}
                      className="absolute right-2 bottom-2 p-2 text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <PaperAirplaneIcon className="h-5 w-5" />
                    </button>
                  </div>
                  
                  {/* Botão de Stop - aparece quando está digitando */}
                  {isTyping && (
                    <button
                      onClick={handleStopGeneration}
                      className="p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                      title="Interromper geração"
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 24 24" 
                        fill="currentColor" 
                        className="w-5 h-5"
                      >
                        <rect x="6" y="6" width="12" height="12" rx="1" />
                      </svg>
                    </button>
                  )}
                  
                  {/* Botão de voz - só aparece quando NÃO está digitando */}
                  {!isTyping && (
                    <VoiceButton
                      onVoiceResult={handleVoiceResult}
                      onVoiceError={handleVoiceError}
                      disabled={!activeThreadId || isTyping || privateUploading}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <Modal
        isOpen={!!agentIntentModal}
        title="Mencionar outro agente"
        onClose={() => {
          agentIntentModal?.resolve('cancel');
          setAgentIntentModal(null);
        }}
      >
        {agentIntentModal ? (
          <div>
            <p className="text-gray-700 text-sm mb-2">
              Você escolheu <strong>{agentIntentModal.workspace.assistantName}</strong>, diferente do agente desta conversa (
              {workspace?.assistantName ?? '—'}).
            </p>
            <p className="text-gray-600 text-xs mb-4 leading-relaxed">
              Deseja abrir uma nova conversa vinculada ao agente mencionado ou pedir uma resposta pontual usando a base deste agente, mantendo
              a thread atual? Orquestração multi-agente, ferramentas externas por agente e fluxos operacionais entre agentes permanecem como
              evolução futura.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  agentIntentModal.resolve('pontual');
                  setAgentIntentModal(null);
                }}
              >
                Resposta pontual
              </Button>
              <Button
                variant="primary"
                type="button"
                onClick={() => {
                  agentIntentModal.resolve('new-thread');
                  setAgentIntentModal(null);
                }}
              >
                Nova conversa com este agente
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Document Preview Modal */}
      {searchPreview.isOpen && searchPreview.document && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                {getFileIcon(searchPreview.document.nodeType)}
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {searchPreview.document.title}
                  </h3>
                  <p className="text-sm text-gray-500">{searchPreview.document.path}</p>
                </div>
              </div>
              <button
                onClick={closeDocumentPreview}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Document Info */}
                <div className="lg:col-span-1">
                  <h4 className="font-medium text-gray-900 mb-4">Informações do Documento</h4>
                  <div className="space-y-3">
                    <div className="flex items-center text-sm">
                      <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">
                        Criado em: {searchPreview.document.metadata?.createdAt ? formatDate(searchPreview.document.metadata.createdAt) : 'Data não disponível'}
                      </span>
                    </div>
                    <div className="flex items-center text-sm">
                      <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">
                        Autor: {searchPreview.document.metadata?.author || searchPreview.document.metadata?.createdBy || 'Sistema'}
                      </span>
                    </div>
                    <div className="flex items-center text-sm">
                      <DocumentIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">Tipo: {searchPreview.document.metadata?.contentType || searchPreview.document.nodeType}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <FolderIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">Caminho: {searchPreview.document.path}</span>
                    </div>
                  </div>
                </div>

                {/* Document Content */}
                <div className="lg:col-span-2">
                  <h4 className="font-medium text-gray-900 mb-4">Conteúdo</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-700">
                      <p>Conteúdo não disponível para este documento.</p>
                      <p className="mt-2">
                        O assistente virtual pode ajudar a navegar por este documento, 
                        responder perguntas sobre seu conteúdo ou sugerir documentos relacionados.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-500">
                Use o chat para fazer perguntas sobre este documento
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => askAboutDocument(searchPreview.document!)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  Perguntar sobre este documento
                </button>
                <button
                  onClick={closeDocumentPreview}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ChatInDocPage; 